"use client";

import { c as _c } from "react/compiler-runtime";
import type { Genre, Premiere } from "@filmweb/sanity-types";
import classNames from "classnames";
import React, { Suspense } from "react";
import CinemaFilterSlot from "src/components/film/program/CinemaFilterSlot";
import ProgramShows from "src/components/film/program/ProgramShows";
import Selectors from "src/components/film/program/Selectors";
import ShowDatesLoader from "src/components/film/program/ShowDatesLoader";
import { type MovieSearchParams } from "src/components/film/program/ShowsForDate";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import ProgramDateButtonRow from "src/components/programdatebutton/ProgramDateButtonRow";
import { useProgramDatesContext } from "src/lib/contexts/ContextTypes";
import styles from './ProgramDisplay.module.css';
type SponsorWithClassName = {
  className?: string;
};

//#region [Props]
type ProgramDisplayProps = {
  edi?: string | null;
  movieTitle?: string | null;
  /**
   * When set (and edi is not), showtimes are searched for by title instead of by id -
   * used for temporary /program/film pages for movies without a movieId yet.
   */
  movieTitleSearch?: string | null;
  premiere?: Premiere | null;
  children?: React.ReactNode;
  genres?: Genre[];
  isKinoklubbMovie?: boolean;
  className?: string;
  sponsor?: React.ReactElement<SponsorWithClassName>;
  isAdFree?: boolean;
  adFreeSkin?: string | null;
};
//#endregion

//#region [Component]
/**
 * The program for one movie: location selector, date row, cinema filter and the shows.
 * The parts that depend on the url (?firm=, ?theatre=) are in their own Suspense boundaries, so the
 * controls can be prerendered while the search params are not known yet (Cache Components).
 */
export default function ProgramDisplay(t0) {
  const $ = _c(50);
  const {
    edi,
    movieTitle,
    movieTitleSearch,
    premiere,
    genres,
    isKinoklubbMovie: t1,
    children,
    className,
    sponsor,
    isAdFree,
    adFreeSkin
  } = t0;
  const isKinoklubbMovie = t1 === undefined ? false : t1;
  const datesContext = useProgramDatesContext();
  const {
    availableDates,
    selectedDate,
    showingAllDates
  } = datesContext;
  let t2;
  if ($[0] !== edi || $[1] !== movieTitleSearch) {
    t2 = edi ? {
      movieId: edi
    } : movieTitleSearch ? {
      movieTitle: movieTitleSearch
    } : null;
    $[0] = edi;
    $[1] = movieTitleSearch;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const movieParams = t2;
  if (!movieTitle || !movieParams) {
    return null;
  }
  let t3;
  if ($[3] !== className) {
    t3 = classNames(styles.SProgramWrapper, className);
    $[3] = className;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== adFreeSkin || $[6] !== isAdFree || $[7] !== movieParams) {
    t4 = <Suspense fallback={null}><ShowDatesLoader movieParams={movieParams} isAdFree={isAdFree} adFreeSkin={adFreeSkin} /></Suspense>;
    $[5] = adFreeSkin;
    $[6] = isAdFree;
    $[7] = movieParams;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = !!sponsor;
  let t6;
  if ($[9] !== t5) {
    t6 = classNames(styles.SControlsWrapper, {
      [styles.withSponsor]: t5
    });
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== sponsor) {
    t7 = sponsor && React.Children.map(sponsor, spnsr => {
      if (React.isValidElement(spnsr)) {
        return React.cloneElement(spnsr, {
          className: classNames(sponsor.props.className, "TopOfPageSponsor")
        });
      }
      return spnsr;
    });
    $[11] = sponsor;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = !!sponsor;
  let t9;
  if ($[13] !== t8) {
    t9 = classNames(styles.SSelectors, {
      [styles.withSponsor]: t8
    });
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = <Selectors className={t9} />;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = !!sponsor;
  let t12;
  if ($[17] !== t11) {
    t12 = classNames(styles.DateSelectors, {
      [styles.withSponsor]: t11
    });
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== availableDates || $[20] !== datesContext.setSelectedDate || $[21] !== selectedDate || $[22] !== showingAllDates || $[23] !== t12) {
    t13 = <ProgramDateButtonRow dates={availableDates} selectedDate={selectedDate} isAllSelected={showingAllDates} scrollContainerClassName={t12} onButtonClick={datesContext.setSelectedDate} />;
    $[19] = availableDates;
    $[20] = datesContext.setSelectedDate;
    $[21] = selectedDate;
    $[22] = showingAllDates;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== adFreeSkin || $[26] !== isAdFree) {
    t14 = <Suspense fallback={null}><CinemaFilterSlot isAdFree={isAdFree} adFreeSkin={adFreeSkin} /></Suspense>;
    $[25] = adFreeSkin;
    $[26] = isAdFree;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t10 || $[29] !== t13 || $[30] !== t14 || $[31] !== t6 || $[32] !== t7) {
    t15 = <div className={t6}>{t7}{t10}{t13}{t14}</div>;
    $[28] = t10;
    $[29] = t13;
    $[30] = t14;
    $[31] = t6;
    $[32] = t7;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== adFreeSkin || $[35] !== edi || $[36] !== genres || $[37] !== isAdFree || $[38] !== isKinoklubbMovie || $[39] !== movieParams || $[40] !== movieTitle || $[41] !== premiere || $[42] !== selectedDate) {
    t16 = selectedDate && <div className={styles.SShowWrapper}><Suspense fallback={<LoadingIndicator />}><ProgramShows movieParams={movieParams} edi={edi} movieTitle={movieTitle} premiere={premiere} genres={genres} isKinoklubbMovie={isKinoklubbMovie} isAdFree={isAdFree} adFreeSkin={adFreeSkin} /></Suspense></div>;
    $[34] = adFreeSkin;
    $[35] = edi;
    $[36] = genres;
    $[37] = isAdFree;
    $[38] = isKinoklubbMovie;
    $[39] = movieParams;
    $[40] = movieTitle;
    $[41] = premiere;
    $[42] = selectedDate;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== children || $[45] !== t15 || $[46] !== t16 || $[47] !== t3 || $[48] !== t4) {
    t17 = <div className={t3}>{t4}{t15}{t16}{children}</div>;
    $[44] = children;
    $[45] = t15;
    $[46] = t16;
    $[47] = t3;
    $[48] = t4;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  return t17;
}
//#endregion

//#region [Styles]
// Converted to CSS Modules (ProgramDisplay.module.css)
//#endregion